
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import Swal from "sweetalert2";
import { useTracker } from "../../contexts/TrackerContext";
import {
  TRACKER_CALL_TYPE_OPTIONS,
  TRACKER_COVERAGE_OPTIONS,
  TRACKER_SELECT_QUERY_KEY,
  TRACKER_SELECT_QUERY_VALUE,
  TRACKER_SOURCE_OPTIONS,
  TRACKER_VALID_ID_OPTIONS,
} from "../../utils/trackerBridge";
import ActiveTicketWarningModal from "./ActiveTicketWarningModal";
import TrackerAttachmentUploader from "./TrackerAttachmentUploader";
import TrackerQueueEditModal from "./TrackerQueueEditModal";
import TrackerTakeOverWarningModal from "./TrackerTakeOverWarningModal";
import TrackerTakeoverHistoryModal from "./TrackerTakeoverHistoryModal";
import { trackerToast as toast } from "./trackerToast";

const INPUT_CLASS =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500";

const TABS = ["new", "queue", "browse"];
const PAGINATION_OPTIONS = [5, 10, 20, 50, 100];
const FILTER_DEBOUNCE_MS = 450;
const CREATOR_EDIT_FORBIDDEN_MESSAGE =
  "Sorry, Only MO who created the ticket should be allowed to edit it.";
const ACTION_CONFIRMATIONS = {
  newTicket: {
    title: "Create New Ticket?",
    message: "This will create a new tracker ticket before you can encode details. Do you want to proceed?",
    confirmLabel: "Yes, Create",
    confirmClassName: "bg-emerald-600 hover:bg-emerald-700",
  },
  save: {
    title: "Save Changes?",
    message: "Are you sure you want to save the current details of this ticket?",
    confirmLabel: "Yes, Save",
    confirmClassName: "bg-blue-600 hover:bg-blue-700",
  },
  generate: {
    title: "Generate TRN?",
    message:
      "This will generate a TRN and redirect you to the LOA Transaction module. Do you want to proceed?",
    confirmLabel: "Yes, Generate",
    confirmClassName: "bg-indigo-600 hover:bg-indigo-700",
  },
  queue: {
    title: "Move to Queue?",
    message: "Are you sure you want to move this ticket to the queue?",
    confirmLabel: "Yes, Queue",
    confirmClassName: "bg-amber-600 hover:bg-amber-700",
  },
  close: {
    title: "Close Ticket?",
    message: "Are you sure you want to close this tracker? Any unsaved changes will be lost.",
    confirmLabel: "Yes, Close",
    confirmClassName: "bg-rose-600 hover:bg-rose-700",
  },
};

const FieldLabel = ({ children, required = false }) => (
  <span>
    {children}
    {required ? <span className="ml-1 text-red-600">*</span> : null}
  </span>
);

function range(min, max, pad = false) {
  const out = [];
  for (let value = min; value <= max; value += 1) {
    out.push(pad ? `${value}`.padStart(2, "0") : `${value}`);
  }
  return out;
}

const HOURS = range(1, 12);
const MINUTES = range(0, 59, true);

const TrackerFormInput = ({
  label,
  value,
  onChange,
  readOnly = false,
  type = "text",
  placeholder = "",
  required = false,
  className = "",
}) => (
  <label className={`text-xs font-semibold uppercase tracking-wide text-slate-500 ${className}`}>
    <FieldLabel required={required}>{label}</FieldLabel>
    <input
      type={type}
      value={value || ""}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      className={`${INPUT_CLASS} ${readOnly ? "bg-slate-100" : ""}`}
    />
  </label>
);

const TrackerActionConfirmModal = ({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmClassName,
  isSubmitting = false,
  onCancel,
  onConfirm,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-slate-700">{message}</p>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className={`rounded-lg px-3 py-2 text-sm font-semibold text-white ${confirmClassName} disabled:cursor-not-allowed disabled:opacity-70`}
          >
            {isSubmitting ? "Processing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const SortableHeader = ({ label, sortKey, sortBy, sortDirection, onSort }) => {
  const active = sortBy === sortKey;
  const normalizedDirection = String(sortDirection || "asc").toLowerCase();
  const indicator = active ? (normalizedDirection === "asc" ? "ASC" : "DESC") : "SORT";

  return (
    <th
      className="px-3 py-2 text-left font-semibold text-slate-700"
      aria-sort={active ? (normalizedDirection === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex w-full cursor-pointer items-center gap-1 text-left hover:text-blue-700"
      >
        <span>{label}</span>
        <span className={`text-[10px] ${active ? "text-blue-700" : "text-slate-400"}`}>
          {indicator}
        </span>
      </button>
    </th>
  );
};

function getBrowseDateRangeError(filters) {
  const dateFrom = String(filters?.date_fr || "").trim();
  const dateTo = String(filters?.date_to || "").trim();

  if (dateFrom && dateTo && dateFrom > dateTo) {
    return "Date From cannot be later than Date To. Please select a valid date range.";
  }

  return "";
}

function getStatusBadgeClass(status) {
  const normalized = String(status || "").toLowerCase();

  if (normalized === "open") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }

  if (normalized === "queued" || normalized === "queue") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }

  if (normalized === "closed") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }

  return "border-slate-200 bg-slate-50 text-slate-700";
}

function isQueuedStatus(status) {
  const normalized = String(status || "").toLowerCase();
  return normalized === "queued" || normalized === "queue";
}

const TrackerPanel = ({ mode = "split" }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    draft,
    currentTicket,
    attachments,
    queueTickets,
    queueFilters,
    queuePagination,
    queueQueryDebug,
    browseRows,
    browseFilters,
    browsePagination,
    browseQueryDebug,
    loadingCurrent,
    loadingQueue,
    loadingBrowse,
    savingDraft,
    uploadingAttachments,
    activeTicketWarning,
    setActiveTicketWarning,
    normalizeQueueTicketDraft,

    loadCurrentTicket,
    fetchQueueTickets,
    fetchTicketByNumber,
    fetchTakeoverHistory,
    saveCurrentTicket,
    createNewTicket,
    queueCurrentTicket,
    takeOverTicket,
    editCreatorTicket,
    closeCurrentTicket,

    saveQueueTicketDraft,
    closeQueueTicketDraft,

    uploadTicketAttachments,
    deleteTicketAttachment,
    refreshTicketAttachmentUrl,

    fetchBrowseTickets,
    exportBrowseTickets,
    setBrowseFilters,
    setQueueFilters,

    setDraftField,
    prepareGenerateLoaPrefill,
  } = useTracker();

  const [activeTab, setActiveTab] = useState("new");
  const [newActionBusy, setNewActionBusy] = useState(false);
  const [browseSearch, setBrowseSearch] = useState(browseFilters.search_string || "");
  const [browseDateRangeError, setBrowseDateRangeError] = useState("");

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [queueModalLoading, setQueueModalLoading] = useState(false);
  const [queueModalSaving, setQueueModalSaving] = useState(false);
  const [queueModalDraft, setQueueModalDraft] = useState(null);
  const [queueModalAttachments, setQueueModalAttachments] = useState([]);
  const [queueModalMode, setQueueModalMode] = useState("edit");
  const [queueModalMeta, setQueueModalMeta] = useState(null);
  const [takeOverPrompt, setTakeOverPrompt] = useState(null);
  const [takeOverBusy, setTakeOverBusy] = useState(false);
  const [creatorEditBusyTicket, setCreatorEditBusyTicket] = useState("");
  const [actionConfirm, setActionConfirm] = useState(null);
  const [actionConfirmBusy, setActionConfirmBusy] = useState(false);
  const [queueTicketNumberFilter, setQueueTicketNumberFilter] = useState(queueFilters.ticket_number || "");
  const [queueMemberNameFilter, setQueueMemberNameFilter] = useState(queueFilters.member_name || "");
  const [queueCoverageFilter, setQueueCoverageFilter] = useState(queueFilters.coverage || "");
  const [queueCreatedByFilter, setQueueCreatedByFilter] = useState(queueFilters.created_by || "");
  const [queueTakenOverByFilter, setQueueTakenOverByFilter] = useState(
    queueFilters.taken_over_by || ""
  );
  const [browseTicketNumberFilter, setBrowseTicketNumberFilter] = useState(
    browseFilters.ticket_number || ""
  );
  const [browseMemberNameFilter, setBrowseMemberNameFilter] = useState(browseFilters.member_name || "");
  const [browseCoverageFilter, setBrowseCoverageFilter] = useState(browseFilters.coverage || "");
  const [browseCreatedByFilter, setBrowseCreatedByFilter] = useState(browseFilters.created_by || "");
  const [browseTakenOverByFilter, setBrowseTakenOverByFilter] = useState(
    browseFilters.taken_over_by || ""
  );
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyModalLoading, setHistoryModalLoading] = useState(false);
  const [historyTicketNumber, setHistoryTicketNumber] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyQueryDebug, setHistoryQueryDebug] = useState(null);
  const queueCoverageDatalistId = "tracker-queue-coverage-options";
  const browseCoverageDatalistId = "tracker-browse-coverage-options";

  useEffect(() => {
    fetchQueueTickets({ silent: true });
  }, [fetchQueueTickets]);

  useEffect(() => {
    setBrowseSearch(browseFilters.search_string || "");
  }, [browseFilters.search_string]);

  useEffect(() => {
    setQueueTicketNumberFilter(queueFilters.ticket_number || "");
    setQueueMemberNameFilter(queueFilters.member_name || "");
    setQueueCoverageFilter(queueFilters.coverage || "");
    setQueueCreatedByFilter(queueFilters.created_by || "");
    setQueueTakenOverByFilter(queueFilters.taken_over_by || "");
  }, [
    queueFilters.ticket_number,
    queueFilters.member_name,
    queueFilters.coverage,
    queueFilters.created_by,
    queueFilters.taken_over_by,
  ]);

  useEffect(() => {
    setBrowseTicketNumberFilter(browseFilters.ticket_number || "");
    setBrowseMemberNameFilter(browseFilters.member_name || "");
    setBrowseCoverageFilter(browseFilters.coverage || "");
    setBrowseCreatedByFilter(browseFilters.created_by || "");
    setBrowseTakenOverByFilter(browseFilters.taken_over_by || "");
  }, [
    browseFilters.ticket_number,
    browseFilters.member_name,
    browseFilters.coverage,
    browseFilters.created_by,
    browseFilters.taken_over_by,
  ]);

  const activeTicketLabel = useMemo(() => {
    if (!currentTicket?.tkt_num) return "No active ticket";
    return `#${currentTicket.tkt_num}`;
  }, [currentTicket?.tkt_num]);

  const hasActiveTicket = Boolean(currentTicket?.tkt_num);
  const currentStatusLabel = hasActiveTicket ? currentTicket?.status || "open" : "No active ticket";

  const updateField = (field, value) => {
    if (!hasActiveTicket) return;
    setDraftField(field, value);
  };

  const openMemberSelector = () => {
    if (!hasActiveTicket) {
      toast.info("Create a tracker ticket before selecting a member.");
      return;
    }

    const returnPath = `${location.pathname}${location.search || ""}`;
    navigate(
      `/members?${TRACKER_SELECT_QUERY_KEY}=${TRACKER_SELECT_QUERY_VALUE}&return_to=${encodeURIComponent(
        returnPath
      )}`
    );
  };

  const openProviderSelector = () => {
    if (!hasActiveTicket) {
      toast.info("Create a tracker ticket before selecting a provider.");
      return;
    }

    const returnPath = `${location.pathname}${location.search || ""}`;
    navigate(
      `/medical/provider?${TRACKER_SELECT_QUERY_KEY}=${TRACKER_SELECT_QUERY_VALUE}&return_to=${encodeURIComponent(
        returnPath
      )}`
    );
  };

  const startNewTicket = async () => {
    try {
      setNewActionBusy(true);
      const result = await createNewTicket();
      if (!result?.requiresAction) {
        setActiveTab("new");
      }
    } catch (error) {
      if (!error?.toastShown) {
        toast.error(error?.message || "Failed to create a new tracker ticket.");
      }
    } finally {
      setNewActionBusy(false);
    }
  };

  const resolveActiveTicket = async (action) => {
    try {
      setNewActionBusy(true);
      await createNewTicket({ activeAction: action });
      setActiveTab("new");
    } catch (error) {
      // toast handled in context
    } finally {
      setNewActionBusy(false);
    }
  };

  const openGenerateTrn = async () => {
    if (!draft.tkt_num) {
      toast.error("Create or load a tracker ticket first.");
      return;
    }

    try {
      await saveCurrentTicket({ silent: true });
    } catch (error) {
      toast.error(error?.message || "Unable to save ticket before Generate TRN.");
      return;
    }

    const returnPath = `${location.pathname}${location.search || ""}`;
    prepareGenerateLoaPrefill(returnPath);
    navigate("/generate-loa-transaction?from_tracker=1");
  };

  const refreshQueueModal = async (tktNum, { forBrowse = true } = {}) => {
    const data = await fetchTicketByNumber(tktNum, { forBrowse });
    if (!data?.ticket) {
      const message = "Tracker ticket details not found.";
      toast.error(message);
      throw new Error(message);
    }

    setQueueModalDraft(normalizeQueueTicketDraft(data.ticket));
    setQueueModalMeta(data.ticket || null);
    setQueueModalAttachments(data.attachments || []);
  };

  const openQueueModal = async (tktNum) => {
    try {
      setQueueModalMode("view");
      setQueueModalMeta(null);
      setQueueModalOpen(true);
      setQueueModalLoading(true);
      await refreshQueueModal(tktNum, { forBrowse: true });
    } catch (error) {
      setQueueModalOpen(false);
    } finally {
      setQueueModalLoading(false);
    }
  };

  const openBrowseViewModal = async (tktNum) => {
    try {
      setQueueModalMode("view");
      setQueueModalMeta(null);
      setQueueModalOpen(true);
      setQueueModalLoading(true);
      await refreshQueueModal(tktNum, { forBrowse: true });
    } catch (error) {
      setQueueModalOpen(false);
    } finally {
      setQueueModalLoading(false);
    }
  };

  const handleSaveCurrentTicket = async () => {
    try {
      await saveCurrentTicket();
    } catch (error) {
      // toast handled in context
    }
  };

  const handleQueueCurrentTicket = async () => {
    try {
      await queueCurrentTicket();
    } catch (error) {
      // toast handled in context
    }
  };

  const handleCloseCurrentTicket = async () => {
    try {
      await closeCurrentTicket();
    } catch (error) {
      // toast handled in context
    }
  };

  const requestActionConfirmation = (action) => {
    const confirmation = ACTION_CONFIRMATIONS[action];
    if (!confirmation) return;
    setActionConfirm({ action, ...confirmation });
  };

  const cancelActionConfirmation = () => {
    if (actionConfirmBusy) return;
    setActionConfirm(null);
  };

  const runConfirmedAction = async () => {
    if (!actionConfirm || actionConfirmBusy) return;

    const actionHandlers = {
      newTicket: startNewTicket,
      save: handleSaveCurrentTicket,
      generate: openGenerateTrn,
      queue: handleQueueCurrentTicket,
      close: handleCloseCurrentTicket,
    };
    const handler = actionHandlers[actionConfirm.action];

    if (!handler) {
      setActionConfirm(null);
      return;
    }

    try {
      setActionConfirmBusy(true);
      await handler();
      setActionConfirm(null);
    } finally {
      setActionConfirmBusy(false);
    }
  };

  const applyBrowseFilters = async (patch = {}) => {
    const nextFilters = {
      ...browseFilters,
      ...patch,
    };

    const dateRangeError = getBrowseDateRangeError(nextFilters);
    if (dateRangeError) {
      setBrowseFilters(nextFilters);
      setBrowseDateRangeError(dateRangeError);
      toast.error(dateRangeError);
      return;
    }

    setBrowseDateRangeError("");
    setBrowseFilters(nextFilters);
    await fetchBrowseTickets(nextFilters);
  };

  const applyQueueFilters = async (patch = {}) => {
    const nextFilters = {
      ...queueFilters,
      ...patch,
    };
    setQueueFilters(nextFilters);
    await fetchQueueTickets({ filters: nextFilters });
  };

  const getNextSortDirection = (filters, sortKey) => {
    const isActiveSort = filters.sort_by === sortKey;
    const currentDirection = String(filters.sort_direction || "asc").toLowerCase();
    return isActiveSort && currentDirection === "asc" ? "desc" : "asc";
  };

  const sortQueueBy = async (sortKey) => {
    await applyQueueFilters({
      sort_by: sortKey,
      sort_direction: getNextSortDirection(queueFilters, sortKey),
      page: 1,
    });
  };

  const sortBrowseBy = async (sortKey) => {
    await applyBrowseFilters({
      sort_by: sortKey,
      sort_direction: getNextSortDirection(browseFilters, sortKey),
      page: 1,
    });
  };

  const clearQueueSearchFilters = async () => {
    setQueueTicketNumberFilter("");
    setQueueMemberNameFilter("");
    setQueueCoverageFilter("");
    setQueueCreatedByFilter("");
    setQueueTakenOverByFilter("");
    await applyQueueFilters({
      ticket_number: "",
      member_name: "",
      coverage: "",
      created_by: "",
      taken_over_by: "",
      status: "",
      page: 1,
    });
  };

  const clearBrowseSearchFilters = async () => {
    setBrowseSearch("");
    setBrowseTicketNumberFilter("");
    setBrowseMemberNameFilter("");
    setBrowseCoverageFilter("");
    setBrowseCreatedByFilter("");
    setBrowseTakenOverByFilter("");
    await applyBrowseFilters({
      search_string: "",
      ticket_number: "",
      member_name: "",
      coverage: "",
      status: "",
      created_by: "",
      taken_over_by: "",
      date_fr: "",
      date_to: "",
      source: "",
      page: 1,
    });
  };

  useEffect(() => {
    if (activeTab !== "queue") return undefined;

    const nextFilters = {
      ticket_number: queueTicketNumberFilter,
      member_name: queueMemberNameFilter,
      coverage: queueCoverageFilter,
      created_by: queueCreatedByFilter,
      taken_over_by: queueTakenOverByFilter,
    };

    const filtersChanged =
      nextFilters.ticket_number !== (queueFilters.ticket_number || "") ||
      nextFilters.member_name !== (queueFilters.member_name || "") ||
      nextFilters.coverage !== (queueFilters.coverage || "") ||
      nextFilters.created_by !== (queueFilters.created_by || "") ||
      nextFilters.taken_over_by !== (queueFilters.taken_over_by || "");

    if (!filtersChanged) return undefined;

    const timer = setTimeout(() => {
      applyQueueFilters({
        ...nextFilters,
        page: 1,
      });
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    activeTab,
    queueTicketNumberFilter,
    queueMemberNameFilter,
    queueCoverageFilter,
    queueCreatedByFilter,
    queueTakenOverByFilter,
    queueFilters.ticket_number,
    queueFilters.member_name,
    queueFilters.coverage,
    queueFilters.created_by,
    queueFilters.taken_over_by,
  ]);

  useEffect(() => {
    if (activeTab !== "browse") return undefined;

    const nextFilters = {
      search_string: browseSearch,
      ticket_number: browseTicketNumberFilter,
      member_name: browseMemberNameFilter,
      coverage: browseCoverageFilter,
      created_by: browseCreatedByFilter,
      taken_over_by: browseTakenOverByFilter,
    };

    const filtersChanged =
      nextFilters.search_string !== (browseFilters.search_string || "") ||
      nextFilters.ticket_number !== (browseFilters.ticket_number || "") ||
      nextFilters.member_name !== (browseFilters.member_name || "") ||
      nextFilters.coverage !== (browseFilters.coverage || "") ||
      nextFilters.created_by !== (browseFilters.created_by || "") ||
      nextFilters.taken_over_by !== (browseFilters.taken_over_by || "");

    if (!filtersChanged) return undefined;

    const timer = setTimeout(() => {
      applyBrowseFilters({
        ...nextFilters,
        status: browseFilters.status || "",
        page: 1,
      });
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [
    activeTab,
    browseSearch,
    browseTicketNumberFilter,
    browseMemberNameFilter,
    browseCoverageFilter,
    browseCreatedByFilter,
    browseTakenOverByFilter,
    browseFilters.search_string,
    browseFilters.ticket_number,
    browseFilters.member_name,
    browseFilters.coverage,
    browseFilters.created_by,
    browseFilters.taken_over_by,
    browseFilters.status,
  ]);

  const openTakeOverPrompt = (row, source = "queue") => {
    if (!row?.tkt_num) return;
    setTakeOverPrompt({
      tkt_num: row.tkt_num,
      status: row.tq_status || row.status || "",
      created_by_name: row.created_by_name || "-",
      source,
    });
  };

  const confirmTakeOver = async (remarks = "") => {
    if (!takeOverPrompt?.tkt_num) return;
    try {
      setTakeOverBusy(true);
      await takeOverTicket(takeOverPrompt.tkt_num, {
        remarks: remarks && String(remarks).trim() !== "" ? String(remarks).trim() : null,
      });
      setActiveTab("new");
      await fetchQueueTickets({ silent: true });
      if (takeOverPrompt.source === "browse") {
        await fetchBrowseTickets({
          ...browseFilters,
          page: browsePagination.page || 1,
          per_page: browsePagination.per_page || browseFilters.per_page || 10,
        });
      }
      setTakeOverPrompt(null);
    } catch (error) {
      // toast handled in context
    } finally {
      setTakeOverBusy(false);
    }
  };

  const showCreatorEditErrorModal = (message) => {
    Swal.fire({
      icon: "error",
      title: "Unable to Edit Ticket",
      text: message || CREATOR_EDIT_FORBIDDEN_MESSAGE,
      confirmButtonText: "OK",
      background: "#ffffff",
      customClass: {
        popup: "rounded-2xl shadow-xl",
        title: "text-slate-800 font-semibold",
        content: "text-slate-600",
      },
    });
  };

  const openCreatorEdit = async (row, source = "queue") => {
    if (!row?.tkt_num || creatorEditBusyTicket) return;

    try {
      setCreatorEditBusyTicket(row.tkt_num);
      await editCreatorTicket(row.tkt_num, { silentToast: true });
      setActiveTab("new");
      await fetchQueueTickets({ silent: true });
      if (source === "browse") {
        await fetchBrowseTickets({
          ...browseFilters,
          page: browsePagination.page || 1,
          per_page: browsePagination.per_page || browseFilters.per_page || 10,
        });
      }
      toast.success(`Ticket #${row.tkt_num} loaded for editing.`);
    } catch (error) {
      const message =
        error?.response?.status === 403
          ? CREATOR_EDIT_FORBIDDEN_MESSAGE
          : error?.message || "Unable to load tracker ticket for editing.";
      showCreatorEditErrorModal(message);
    } finally {
      setCreatorEditBusyTicket("");
    }
  };

  const openTakeoverHistoryModal = async (tktNum) => {
    if (!tktNum) return;

    try {
      setHistoryModalOpen(true);
      setHistoryModalLoading(true);
      setHistoryTicketNumber(tktNum);

      const result = await fetchTakeoverHistory(tktNum, { debug: true });
      setHistoryRows(result.rows || []);
      setHistoryQueryDebug(result.queryDebug || null);
    } catch (error) {
      setHistoryModalOpen(false);
    } finally {
      setHistoryModalLoading(false);
    }
  };

  return (
    <div
      className={`flex min-h-0 flex-col ${
        mode === "page" ? "h-[calc(100vh-10rem)] min-h-[30rem]" : "h-full"
      }`}
    >
      <div className="flex h-full min-h-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-[0.15em] text-slate-600">Tracker</h2>
              <p className="text-lg font-semibold text-slate-900">{activeTicketLabel}</p>
            </div>
            <div className="flex items-center gap-2">
              {mode !== "page" ? (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => navigate("/tracker")}
                    className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                  >
                    View Tracker Full Page
                  </button>
                  <span className="pointer-events-none absolute -right-2 -top-2 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none text-white">
                    New
                  </span>
                </div>
              ) : null}
              <button
                type="button"
                onClick={() => requestActionConfirmation("newTicket")}
                disabled={newActionBusy || actionConfirmBusy}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-70 ${
                  hasActiveTicket
                    ? "bg-slate-900 hover:bg-slate-800"
                    : "animate-pulse bg-emerald-600 shadow-lg shadow-emerald-100 ring-4 ring-emerald-100 hover:bg-emerald-700"
                }`}
              >
                <span className="grid h-5 w-5 place-items-center rounded-full bg-white/20 text-base leading-none">
                  +
                </span>
                <span>New Ticket</span>
              </button>
              <button
                type="button"
                onClick={() => loadCurrentTicket()}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="border-b border-slate-200 px-4 pt-3">
          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`rounded-t-lg px-3 py-2 text-sm font-semibold capitalize ${
                  activeTab === tab
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
          {activeTab === "new" ? (
            <div className="space-y-4 pb-2">
              <div className="grid gap-3 md:grid-cols-2">
                <TrackerFormInput label="Ticket#" value={draft.tkt_num} readOnly />
                <TrackerFormInput label="Status" value={currentStatusLabel} readOnly />
              </div>

              {!hasActiveTicket ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-800">
                  Create a new tracker ticket before editing details, selecting a member, or uploading attachments.
                </div>
              ) : null}

              <fieldset
                disabled={!hasActiveTicket}
                className={`min-w-0 space-y-4 ${!hasActiveTicket ? "opacity-60" : ""}`}
              >
              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <FieldLabel required>Source</FieldLabel>
                  <select
                    value={draft.source || ""}
                    onChange={(event) => updateField("source", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select source</option>
                    {TRACKER_SOURCE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <FieldLabel required>Call Type</FieldLabel>
                  <select
                    value={draft.call_type || ""}
                    onChange={(event) => updateField("call_type", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select call type</option>
                    {TRACKER_CALL_TYPE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Coverage
                  <select
                    value={draft.coverage || ""}
                    onChange={(event) => updateField("coverage", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select coverage</option>
                    {TRACKER_COVERAGE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <FieldLabel required>Time Hour</FieldLabel>
                  <select
                    value={draft.time_received_hour || ""}
                    onChange={(event) => updateField("time_received_hour", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">-</option>
                    {HOURS.map((hour) => (
                      <option key={hour} value={hour}>
                        {hour}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <FieldLabel required>Time Minute</FieldLabel>
                  <select
                    value={draft.time_received_minute || ""}
                    onChange={(event) => updateField("time_received_minute", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">-</option>
                    {MINUTES.map((minute) => (
                      <option key={minute} value={minute}>
                        {minute}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <FieldLabel required>AM/PM</FieldLabel>
                  <select
                    value={draft.time_received_ampm || ""}
                    onChange={(event) => updateField("time_received_ampm", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">-</option>
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <TrackerFormInput
                  label="Name of Caller"
                  value={draft.caller}
                  onChange={(event) => updateField("caller", event.target.value)}
                />
                <TrackerFormInput
                  label="Caller's Contact#"
                  value={draft.caller_num}
                  onChange={(event) => updateField("caller_num", event.target.value)}
                />
              </div>

              <div className="space-y-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Selected Member
                  <div className="flex gap-2">
                    <input value={draft.member_name || ""} readOnly className={`${INPUT_CLASS} bg-slate-100`} />
                    <button
                      type="button"
                      onClick={openMemberSelector}
                      aria-label="Search Member"
                      title="Search Member"
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      <Search className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Provider
                  <div className="flex gap-2">
                    <input value={draft.provider_name || ""} readOnly className={`${INPUT_CLASS} bg-slate-100`} />
                    <button
                      type="button"
                      onClick={openProviderSelector}
                      aria-label="Search Provider"
                      title="Search Provider"
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      <Search className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </label>
              </div>

              <div className="grid gap-3">
                <TrackerFormInput label="Member ID" value={draft.member_id} readOnly />
                <TrackerFormInput label="Corporate" value={draft.corp_name} readOnly />
                <TrackerFormInput label="Plan Code" value={draft.plan_code} readOnly />
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Valid ID Presented
                  <select
                    value={draft.valid_id || ""}
                    onChange={(event) => updateField("valid_id", event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">Select valid ID</option>
                    {TRACKER_VALID_ID_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <TrackerFormInput
                  label="ID Number"
                  value={draft.id_num}
                  onChange={(event) => updateField("id_num", event.target.value)}
                  className="md:col-span-2"
                />
              </div>

              <div className="grid gap-3">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Given Diagnosis
                  <textarea
                    value={draft.diagnosis || ""}
                    onChange={(event) => updateField("diagnosis", event.target.value)}
                    rows={2}
                    className={INPUT_CLASS}
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Lab Procedures
                  <textarea
                    value={draft.procedure || ""}
                    onChange={(event) => updateField("procedure", event.target.value)}
                    rows={2}
                    className={INPUT_CLASS}
                  />
                </label>
                <TrackerFormInput
                  label="Name of Dr."
                  value={draft.doctor}
                  onChange={(event) => updateField("doctor", event.target.value)}
                />
              </div>

              <div className="grid gap-3">
                <TrackerFormInput label="TRN#" value={draft.trans_no} onChange={(event) => updateField("trans_no", event.target.value)} />
                <TrackerFormInput label="LOA#" value={draft.loa_no} onChange={(event) => updateField("loa_no", event.target.value)} />
                <TrackerFormInput
                  label="Form#"
                  value={draft.form_number}
                  onChange={(event) => updateField("form_number", event.target.value)}
                />
                <TrackerFormInput
                  label="Disapproved Amt"
                  type="number"
                  required
                  value={draft.disapproved_amt}
                  onChange={(event) => updateField("disapproved_amt", event.target.value)}
                />
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <FieldLabel required>Disapproved Remarks</FieldLabel>
                  <textarea
                    value={draft.disapproved_remarks || ""}
                    onChange={(event) => updateField("disapproved_remarks", event.target.value)}
                    rows={2}
                    className={INPUT_CLASS}
                  />
                </label>
              </div>

              <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Remarks
                <textarea
                  value={draft.remarks || ""}
                  onChange={(event) => updateField("remarks", event.target.value)}
                  rows={2}
                  className={INPUT_CLASS}
                />
              </label>

              <TrackerAttachmentUploader
                source={draft.source}
                ticketNumber={draft.tkt_num}
                attachments={attachments}
                uploading={uploadingAttachments}
                onUpload={uploadTicketAttachments}
                onRefresh={refreshTicketAttachmentUrl}
                onDelete={deleteTicketAttachment}
              />
              </fieldset>

            </div>
          ) : null}

          {activeTab === "queue" ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Queued and closed tickets across all users.</span>
                  {loadingQueue ? (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                      <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                      Waiting...
                    </span>
                  ) : null}
                </p>
                <div className="flex items-center gap-2">
                  <select
                    value={queueFilters.status || ""}
                    onChange={(event) => applyQueueFilters({ status: event.target.value, page: 1 })}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
                  >
                    <option value="">Queued + Closed</option>
                    <option value="queued">Queued</option>
                    <option value="closed">Closed</option>
                    <option value="open">Open</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => fetchQueueTickets()}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  >
                    Refresh Queue
                  </button>
                </div>
              </div>

              <div className="grid gap-2 md:grid-cols-5">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Ticket Number
                  <input
                    value={queueTicketNumberFilter}
                    onChange={(event) => setQueueTicketNumberFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="e.g. 00241968"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Member Name
                  <input
                    value={queueMemberNameFilter}
                    onChange={(event) => setQueueMemberNameFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Member name"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Coverage
                  <input
                    list={queueCoverageDatalistId}
                    value={queueCoverageFilter}
                    onChange={(event) => setQueueCoverageFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Select or type coverage"
                  />
                  <datalist id={queueCoverageDatalistId}>
                    {TRACKER_COVERAGE_OPTIONS.map((option) => (
                      <option key={`queue-coverage-${option}`} value={option} />
                    ))}
                  </datalist>
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Created By
                  <input
                    value={queueCreatedByFilter}
                    onChange={(event) => setQueueCreatedByFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Name or user id"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Take Over By
                  <input
                    value={queueTakenOverByFilter}
                    onChange={(event) => setQueueTakenOverByFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Name or user id"
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={clearQueueSearchFilters}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  Clear Queue Filters
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 bg-white text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <SortableHeader
                        label="Ticket#"
                        sortKey="tkt_num"
                        sortBy={queueFilters.sort_by}
                        sortDirection={queueFilters.sort_direction}
                        onSort={sortQueueBy}
                      />
                      <SortableHeader
                        label="Member"
                        sortKey="member_name"
                        sortBy={queueFilters.sort_by}
                        sortDirection={queueFilters.sort_direction}
                        onSort={sortQueueBy}
                      />
                      <SortableHeader
                        label="Coverage"
                        sortKey="coverage"
                        sortBy={queueFilters.sort_by}
                        sortDirection={queueFilters.sort_direction}
                        onSort={sortQueueBy}
                      />
                      <SortableHeader
                        label="Created By"
                        sortKey="created_by_name"
                        sortBy={queueFilters.sort_by}
                        sortDirection={queueFilters.sort_direction}
                        onSort={sortQueueBy}
                      />
                      <SortableHeader
                        label="Take Over By"
                        sortKey="taken_over_by_name"
                        sortBy={queueFilters.sort_by}
                        sortDirection={queueFilters.sort_direction}
                        onSort={sortQueueBy}
                      />
                      <SortableHeader
                        label="Status"
                        sortKey="status"
                        sortBy={queueFilters.sort_by}
                        sortDirection={queueFilters.sort_direction}
                        onSort={sortQueueBy}
                      />
                      <th className="px-3 py-2 text-right font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingQueue ? (
                      <tr>
                        <td className="px-3 py-4 text-slate-500" colSpan={7}>Loading queue...</td>
                      </tr>
                    ) : queueTickets.length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-slate-500" colSpan={7}>No queued tickets.</td>
                      </tr>
                    ) : (
                      queueTickets.map((row) => (
                        <tr key={row.tkt_num}>
                          <td className="px-3 py-2 font-semibold text-slate-900">#{row.tkt_num}</td>
                          <td className="px-3 py-2 text-slate-700">{row.member_name || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.coverage || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.created_by_name || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.taken_over_by_name || "-"}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${getStatusBadgeClass(
                                row.tq_status
                              )}`}
                            >
                              <span>{row.tq_status || "-"}</span>
                              {isQueuedStatus(row.tq_status) ? (
                                <span className="ml-1 h-3 w-3 animate-spin rounded-full border border-amber-300 border-t-amber-700" />
                              ) : null}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex gap-1">
                              <button
                                type="button"
                                onClick={() => openQueueModal(row.tkt_num)}
                                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                              >
                                View
                              </button>
                              {row.can_take_over ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openTakeOverPrompt(row, "queue")}
                                    disabled
                                    className="cursor-not-allowed rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-medium text-slate-400"
                                    title="Take Over is temporarily disabled."
                                  >
                                    Take Over
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openCreatorEdit(row, "queue")}
                                    disabled={Boolean(creatorEditBusyTicket)}
                                    className="rounded-md border border-emerald-300 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {creatorEditBusyTicket === row.tkt_num ? "Loading..." : "Edit"}
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => openTakeoverHistoryModal(row.tkt_num)}
                                className="rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                                title="View takeover history"
                              >
                                <span className="inline-flex items-center gap-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    className="h-3.5 w-3.5"
                                  >
                                    <path d="M12 8v5l3 2" />
                                    <circle cx="12" cy="12" r="9" />
                                  </svg>
                                  History
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {queueQueryDebug?.rows?.resolved ? (
                <details className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  <summary className="cursor-pointer font-semibold text-slate-800">
                    Queue API Debug Query (Resolved)
                  </summary>
                  <div className="mt-2 space-y-2">
                    <div>
                      <p className="font-semibold">Rows Query</p>
                      <pre className="overflow-x-auto whitespace-pre-wrap">{queueQueryDebug.rows.resolved}</pre>
                    </div>
                    <div>
                      <p className="font-semibold">Count Query</p>
                      <pre className="overflow-x-auto whitespace-pre-wrap">
                        {queueQueryDebug.count?.resolved || "-"}
                      </pre>
                    </div>
                  </div>
                </details>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1">
                    <span>Per page</span>
                    <select
                      value={queueFilters.per_page || 10}
                      onChange={(event) =>
                        applyQueueFilters({ per_page: Number(event.target.value), page: 1 })
                      }
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                    >
                      {PAGINATION_OPTIONS.map((option) => (
                        <option key={`queue-pp-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span>
                    Page {queuePagination.page || 1} of {queuePagination.total_pages || 1}
                  </span>
                  <span>Total: {queuePagination.total || 0}</span>
                </div>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      applyQueueFilters({ page: Math.max(1, (queuePagination.page || 1) - 1) })
                    }
                    disabled={(queuePagination.page || 1) <= 1}
                    className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      applyQueueFilters({
                        page: Math.min(
                          queuePagination.total_pages || 1,
                          (queuePagination.page || 1) + 1
                        ),
                      })
                    }
                    disabled={(queuePagination.page || 1) >= (queuePagination.total_pages || 1)}
                    className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : null}
          {activeTab === "browse" ? (
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-5">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Search
                  <input
                    value={browseSearch}
                    onChange={(event) => setBrowseSearch(event.target.value)}
                    placeholder="Ticket, TRN, Member..."
                    className={INPUT_CLASS}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date From
                  <input
                    type="date"
                    value={browseFilters.date_fr || ""}
                    onChange={(event) => applyBrowseFilters({ date_fr: event.target.value, page: 1 })}
                    className={`${INPUT_CLASS} ${
                      browseDateRangeError ? "border-rose-300 bg-rose-50 focus:border-rose-500" : ""
                    }`}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Date To
                  <input
                    type="date"
                    value={browseFilters.date_to || ""}
                    onChange={(event) => applyBrowseFilters({ date_to: event.target.value, page: 1 })}
                    className={`${INPUT_CLASS} ${
                      browseDateRangeError ? "border-rose-300 bg-rose-50 focus:border-rose-500" : ""
                    }`}
                  />
                </label>

                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Source
                  <select
                    value={browseFilters.source || ""}
                    onChange={(event) => applyBrowseFilters({ source: event.target.value, page: 1 })}
                    className={INPUT_CLASS}
                  >
                    <option value="">All Sources</option>
                    {TRACKER_SOURCE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Status
                  <select
                    value={browseFilters.status || ""}
                    onChange={(event) => applyBrowseFilters({ status: event.target.value, page: 1 })}
                    className={INPUT_CLASS}
                  >
                    <option value="">Queued + Closed</option>
                    <option value="queued">Queued</option>
                    <option value="closed">Closed</option>
                    <option value="open">Open</option>
                  </select>
                </label>
              </div>

              {browseDateRangeError ? (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
                  {browseDateRangeError}
                </div>
              ) : null}

              <div className="grid gap-3 md:grid-cols-5">
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Ticket Number
                  <input
                    value={browseTicketNumberFilter}
                    onChange={(event) => setBrowseTicketNumberFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="e.g. 00241968"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Member Name
                  <input
                    value={browseMemberNameFilter}
                    onChange={(event) => setBrowseMemberNameFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Member name"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Coverage
                  <input
                    list={browseCoverageDatalistId}
                    value={browseCoverageFilter}
                    onChange={(event) => setBrowseCoverageFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Select or type coverage"
                  />
                  <datalist id={browseCoverageDatalistId}>
                    {TRACKER_COVERAGE_OPTIONS.map((option) => (
                      <option key={`browse-coverage-${option}`} value={option} />
                    ))}
                  </datalist>
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Created By
                  <input
                    value={browseCreatedByFilter}
                    onChange={(event) => setBrowseCreatedByFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Name or user id"
                  />
                </label>
                <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Take Over By
                  <input
                    value={browseTakenOverByFilter}
                    onChange={(event) => setBrowseTakenOverByFilter(event.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Name or user id"
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={clearBrowseSearchFilters}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                >
                  Clear Filters
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const dateRangeError = getBrowseDateRangeError(browseFilters);
                    if (dateRangeError) {
                      setBrowseDateRangeError(dateRangeError);
                      toast.error(dateRangeError);
                      return;
                    }

                    try {
                      await exportBrowseTickets({
                        search_string: browseSearch,
                        date_fr: browseFilters.date_fr || "",
                        date_to: browseFilters.date_to || "",
                        source: browseFilters.source || "",
                        ticket_number: browseTicketNumberFilter,
                        member_name: browseMemberNameFilter,
                        coverage: browseCoverageFilter,
                        status: browseFilters.status || "",
                        created_by: browseCreatedByFilter,
                        taken_over_by: browseTakenOverByFilter,
                      });
                    } catch (error) {
                      // toast handled in context
                    }
                  }}
                  disabled={Boolean(browseDateRangeError)}
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Download CSV
                </button>
              </div>

              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="min-w-full divide-y divide-slate-200 bg-white text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <SortableHeader
                        label="Ticket#"
                        sortKey="tkt_num"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Created"
                        sortKey="datetime_created"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Member"
                        sortKey="member_name"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Source"
                        sortKey="source"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Coverage"
                        sortKey="coverage"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Created By"
                        sortKey="created_by_name"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Take Over By"
                        sortKey="taken_over_by_name"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <SortableHeader
                        label="Status"
                        sortKey="status"
                        sortBy={browseFilters.sort_by}
                        sortDirection={browseFilters.sort_direction}
                        onSort={sortBrowseBy}
                      />
                      <th className="px-3 py-2 text-right font-semibold text-slate-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {browseDateRangeError ? (
                      <tr>
                        <td colSpan={9} className="px-3 py-4 font-medium text-rose-700">
                          {browseDateRangeError}
                        </td>
                      </tr>
                    ) : loadingBrowse ? (
                      <tr>
                        <td colSpan={9} className="px-3 py-4 text-slate-500">Loading browse data...</td>
                      </tr>
                    ) : browseRows.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-3 py-4 text-slate-500">No records found.</td>
                      </tr>
                    ) : (
                      browseRows.map((row) => (
                        <tr key={`${row.tkt_num}-${row.datetime_created || ""}`}>
                          <td className="px-3 py-2 font-semibold text-slate-900">#{row.tkt_num}</td>
                          <td className="px-3 py-2 text-slate-700">{row.datetime_created || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.member_name || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.source || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.coverage || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.created_by_name || "-"}</td>
                          <td className="px-3 py-2 text-slate-700">{row.taken_over_by_name || "-"}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${getStatusBadgeClass(
                                row.status
                              )}`}
                            >
                              <span>{row.status || "-"}</span>
                              {isQueuedStatus(row.status) ? (
                                <span className="ml-1 h-3 w-3 animate-spin rounded-full border border-amber-300 border-t-amber-700" />
                              ) : null}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="inline-flex gap-1">
                              <button
                                type="button"
                                onClick={() => openBrowseViewModal(row.tkt_num)}
                                className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
                              >
                                View
                              </button>
                              {row.can_take_over ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => openTakeOverPrompt(row, "browse")}
                                    disabled
                                    className="cursor-not-allowed rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-medium text-slate-400"
                                    title="Take Over is temporarily disabled."
                                  >
                                    Take Over
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openCreatorEdit(row, "browse")}
                                    disabled={Boolean(creatorEditBusyTicket)}
                                    className="rounded-md border border-emerald-300 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
                                  >
                                    {creatorEditBusyTicket === row.tkt_num ? "Loading..." : "Edit"}
                                  </button>
                                </>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => openTakeoverHistoryModal(row.tkt_num)}
                                className="rounded-md border border-indigo-300 px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"
                                title="View takeover history"
                              >
                                <span className="inline-flex items-center gap-1">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    className="h-3.5 w-3.5"
                                  >
                                    <path d="M12 8v5l3 2" />
                                    <circle cx="12" cy="12" r="9" />
                                  </svg>
                                  History
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {browseQueryDebug?.rows?.resolved ? (
                <details className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                  <summary className="cursor-pointer font-semibold text-slate-800">
                    Browse API Debug Query (Resolved)
                  </summary>
                  <div className="mt-2 space-y-2">
                    <div>
                      <p className="font-semibold">Rows Query</p>
                      <pre className="overflow-x-auto whitespace-pre-wrap">
                        {browseQueryDebug.rows.resolved}
                      </pre>
                    </div>
                    <div>
                      <p className="font-semibold">Count Query</p>
                      <pre className="overflow-x-auto whitespace-pre-wrap">
                        {browseQueryDebug.count?.resolved || "-"}
                      </pre>
                    </div>
                  </div>
                </details>
              ) : null}

              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <label className="inline-flex items-center gap-1">
                    <span>Per page</span>
                    <select
                      value={browseFilters.per_page || 10}
                      onChange={(event) =>
                        applyBrowseFilters({ per_page: Number(event.target.value), page: 1 })
                      }
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs"
                    >
                      {PAGINATION_OPTIONS.map((option) => (
                        <option key={`browse-pp-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span>
                    Page {browsePagination.page || 1} of {browsePagination.total_pages || 1}
                  </span>
                  <span>Total: {browsePagination.total || 0}</span>
                </div>
                <div className="inline-flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      applyBrowseFilters({ page: Math.max(1, (browsePagination.page || 1) - 1) })
                    }
                    disabled={(browsePagination.page || 1) <= 1}
                    className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      applyBrowseFilters({
                        page: Math.min(
                          browsePagination.total_pages || 1,
                          (browsePagination.page || 1) + 1
                        ),
                      })
                    }
                    disabled={(browsePagination.page || 1) >= (browsePagination.total_pages || 1)}
                    className="rounded-md border border-slate-300 px-2 py-1 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {activeTab === "new" ? (
          <div className="border-t border-slate-200 bg-white px-4 py-3">
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => requestActionConfirmation("save")}
                disabled={!hasActiveTicket || savingDraft || loadingCurrent || actionConfirmBusy}
                className="rounded-lg border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-70"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => requestActionConfirmation("generate")}
                disabled={!hasActiveTicket || savingDraft || loadingCurrent || actionConfirmBusy}
                className="rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-70"
              >
                Generate TRN
              </button>
              <button
                type="button"
                onClick={() => requestActionConfirmation("queue")}
                disabled={!hasActiveTicket || savingDraft || loadingCurrent || actionConfirmBusy}
                className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-70"
              >
                Queue
              </button>
              <button
                type="button"
                onClick={() => requestActionConfirmation("close")}
                disabled={!hasActiveTicket || savingDraft || loadingCurrent || actionConfirmBusy}
                className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-70"
              >
                Close
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <TrackerActionConfirmModal
        isOpen={Boolean(actionConfirm)}
        title={actionConfirm?.title}
        message={actionConfirm?.message}
        confirmLabel={actionConfirm?.confirmLabel}
        confirmClassName={actionConfirm?.confirmClassName}
        isSubmitting={actionConfirmBusy}
        onCancel={cancelActionConfirmation}
        onConfirm={runConfirmedAction}
      />

      <ActiveTicketWarningModal
        isOpen={Boolean(activeTicketWarning)}
        activeTicket={activeTicketWarning}
        onClose={() => setActiveTicketWarning(null)}
        onAction={resolveActiveTicket}
        isSubmitting={newActionBusy}
      />

      <TrackerTakeOverWarningModal
        isOpen={Boolean(takeOverPrompt)}
        ticketNumber={takeOverPrompt?.tkt_num || ""}
        status={takeOverPrompt?.status || ""}
        createdBy={takeOverPrompt?.created_by_name || "-"}
        isSubmitting={takeOverBusy}
        onCancel={() => setTakeOverPrompt(null)}
        onConfirm={confirmTakeOver}
      />

      <TrackerTakeoverHistoryModal
        isOpen={historyModalOpen}
        loading={historyModalLoading}
        ticketNumber={historyTicketNumber}
        rows={historyRows}
        queryDebug={historyQueryDebug}
        onRefresh={async () => {
          if (!historyTicketNumber) return;
          setHistoryModalLoading(true);
          try {
            const result = await fetchTakeoverHistory(historyTicketNumber, { debug: true });
            setHistoryRows(result.rows || []);
            setHistoryQueryDebug(result.queryDebug || null);
          } finally {
            setHistoryModalLoading(false);
          }
        }}
        onClose={() => {
          setHistoryModalOpen(false);
          setHistoryTicketNumber("");
          setHistoryRows([]);
          setHistoryQueryDebug(null);
        }}
      />

      <TrackerQueueEditModal
        isOpen={queueModalOpen}
        loading={queueModalLoading}
        draft={queueModalDraft || {}}
        ticketMeta={queueModalMeta}
        setDraft={setQueueModalDraft}
        attachments={queueModalAttachments}
        mode={queueModalMode}
        onUpload={async (files) => {
          if (!queueModalDraft?.tkt_num) return;
          try {
            await uploadTicketAttachments(files, queueModalDraft.tkt_num);
            await refreshQueueModal(queueModalDraft.tkt_num);
          } catch (error) {
            // toast handled in context
          }
        }}
        onRefresh={async (uploadId) => {
          try {
            const refreshed = await refreshTicketAttachmentUrl(uploadId, { silentToast: true });
            if (!queueModalDraft?.tkt_num) return;
            await refreshQueueModal(queueModalDraft.tkt_num);
            if (refreshed) {
              toast.success("Attachment URL refreshed.");
            } else {
              toast.info("No attachment URL update was returned.");
            }
          } catch (error) {
            toast.error(error?.message || "Unable to refresh attachment URL.");
          }
        }}
        onDelete={async (uploadId) => {
          if (!queueModalDraft?.tkt_num) return;
          try {
            await deleteTicketAttachment(uploadId, queueModalDraft.tkt_num);
            await refreshQueueModal(queueModalDraft.tkt_num);
          } catch (error) {
            // toast handled in context
          }
        }}
        onSave={async () => {
          if (!queueModalDraft?.tkt_num) return;
          try {
            setQueueModalSaving(true);
            await saveQueueTicketDraft(queueModalDraft, "queued", { silentToast: true });
            await refreshQueueModal(queueModalDraft.tkt_num);
            toast.success("Queue ticket saved.");
          } catch (error) {
            toast.error(error?.message || "Unable to save queued ticket.");
          } finally {
            setQueueModalSaving(false);
          }
        }}
        onCloseTicket={async () => {
          if (!queueModalDraft?.tkt_num) return;
          try {
            setQueueModalSaving(true);
            await closeQueueTicketDraft(queueModalDraft, { silentToast: true });
            setQueueModalOpen(false);
            setQueueModalDraft(null);
            setQueueModalMeta(null);
            setQueueModalAttachments([]);
            setQueueModalMode("view");
            await fetchQueueTickets({ silent: true });
            toast.success(`Queue ticket #${queueModalDraft.tkt_num} closed.`);
          } catch (error) {
            toast.error(error?.message || "Unable to close queued ticket.");
          } finally {
            setQueueModalSaving(false);
          }
        }}
        onCancel={() => {
          setQueueModalOpen(false);
          setQueueModalDraft(null);
          setQueueModalMeta(null);
          setQueueModalAttachments([]);
          setQueueModalMode("view");
        }}
        isSaving={queueModalSaving || uploadingAttachments}
      />
    </div>
  );
};

export default TrackerPanel;
